import React from "react";
import { Pie } from "@nivo/pie";
import { useRecoilState } from "recoil";
import { Utils } from "@kido/plots";
import { useTranslation } from "react-i18next";
import { provincesPlain, countriesEs as countries } from "@kido/ui";
import { appThemeSelector } from "../../store/selectors";
import {
  XYPlot,
  HorizontalGridLines,
  XAxis,
  YAxis,
  VerticalBarSeries
} from "react-vis";

const barSeriesConfig = {
  hideLine: true,
  style: {
    line: { stroke: "#ddddd" },
    ticks: { stroke: "#ddddd" },
    text: {
      stroke: "none",
      fill: "#6b6b6b",
      color: "#6b6b6b",
      fontSize: "9pt"
    }
  }
};

const sumHistogram = (histo) => {
  return histo.reduce((a, b) => a + b["uniques__sum"], 0);
};

provincesPlain.push({
  name: "Unknown",
  code: "Unknown",
  id: 0
});

const UniqueVisitorsInternational = ({ data }) => {
  const { t, i18n } = useTranslation();
  const [theme] = useRecoilState(appThemeSelector);
  const locale = i18n.language;
  const pieColors = theme.theme[theme.mode].colors;

  return (
    <div className="page">
      <div className="h-10" />
      <div className="w-full h-px bg-gray-800" />
      <div className="p-5 h-16">
        <div className="text-international mb-2 font-bold">
          {t("unique_visitors_act")}
        </div>
      </div>
      <div className="text-international mx-6 mt-6 mb-5">
        {t("internationals")}
      </div>
      <div className="flex justify-between w-full">
        <div className="divider-35">
          <div className="flex justify-between w-full flex flex-col">
            <div className="w-full h-24 text-sm  m-auto text-center flex flex-col text-avgStay">
              <div className="text-center">
                <i className="fas fa-user"></i>
              </div>
              <div className="mt-2"></div>
              {t("daytime_visitors")}
              <div className="mt-2"></div>
              <span className="block my-2 text-international text-center">
                {data.histoInternationals.length &&
                  Utils.toThousands(
                    locale,
                    sumHistogram(data.histoInternationals)
                  )}
              </span>
            </div>
            <div className="w-full h-24 text-sm  m-auto text-center flex flex-col text-avgStay">
              <div className="text-center">
                <i className="fas fa-clock"></i>
              </div>
              <div className="mt-2"></div>
              {t("avgStay")}
              <div className="mt-2"></div>
              <span className="block my-2 text-international text-center">
                {data.byLengthAll.avgStayAll.international}
              </span>
            </div>
          </div>
        </div>
        <div className="divider-65 h-full">
          <XYPlot width={500} height={200} xType="ordinal">
            <XAxis tickLabelAngle={-90} {...barSeriesConfig} />
            <YAxis
              {...barSeriesConfig}
              tickFormat={(value, index, scale, tickTotal) => {
                return `${scale.tickFormat(tickTotal, "s")(value)}`; // -> e.g. 1.2kWh
              }}
            />
            <HorizontalGridLines />
            <VerticalBarSeries
              colorType="literal"
              data={data.histoInternationals.map((d, i) => ({
                x: d.date,
                y: d.uniques__sum,
                label: d.uniques__sum,
                color: "#ff3b98"
              }))}
              style={{}}
            />
          </XYPlot>
        </div>
      </div>
      <div className="flex justify-between w-full">
        <div className="divider-50">
          <div className="w-100 h-4"></div>
          <div className="text-international mx-6 mt-6 mb-5">
            {t("by_country")}
          </div>
          <div className="w-100 p-2">
            <div className="w-100 flex justify-center">
              {data.country.data.data && (
                <Pie
                  margin={{
                    top: 20,
                    right: 50,
                    bottom: 150,
                    left: 50
                  }}
                  //colors={{ scheme: "dark2" }}
                  colors={pieColors}
                  borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
                  data={data.country.data.data.map((d, i) => {
                    const id = `${
                      countries[data.country.data.labels[i]] ||
                      data.country.data.labels[i]
                    }`;

                    return {
                      value: d,
                      label: `${id} (${Utils.toThousands(locale, d)}%)`,
                      id
                    };
                  })}
                  width={350}
                  height={500}
                  innerRadius={0.5}
                  padAngle={0.7}
                  cornerRadius={1}
                  borderWidth={1}
                  enableRadialLabels={false}
                  slicesLabelsSkipAngle={10}
                  slicesLabelsTextColor="#fff"
                  legends={[
                    {
                      anchor: "bottom-left",
                      direction: "column",
                      translateY: 146,
                      itemWidth: 100,
                      itemHeight: 22,
                      itemTextColor: "#999",
                      symbolSize: 18,
                      symbolShape: "circle"
                    }
                  ]}
                />
              )}
            </div>
          </div>
        </div>
        <div className="divider-50">
          <div className="w-100 h-4"></div>
          <div className="mb-4 text-international mx-6 mt-6">
            {t("daytime_visitors")}
          </div>
          <div className="w-100 p-2">
            <Pie
              margin={{
                top: 20,
                right: 50,
                bottom: 150,
                left: 50
              }}
              data={data.byLengthAll.internationalLengthAll.map((d, i) => {
                return {
                  value: d.uniques,
                  label: `${
                    d.duration_bin === "overnight"
                      ? d.duration_bin
                      : d.duration_bin + " horas"
                  } (${Utils.toThousands(locale, d.uniques)})`,
                  id: d.duration_bin
                };
              })}
              //colors={{ scheme: "dark2" }}
              colors={pieColors}
              borderColor={{
                from: "color",
                modifiers: [["darker", 0.2]]
              }}
              width={350}
              height={500}
              innerRadius={0.5}
              padAngle={0.7}
              cornerRadius={1}
              borderWidth={1}
              enableRadialLabels={false}
              slicesLabelsSkipAngle={10}
              slicesLabelsTextColor="#ffffff"
              legends={[
                {
                  data: data.byLengthAll.nationalLengthAll.map((id, index) => ({
                    label: id
                  })),
                  anchor: "bottom-left",
                  direction: "column",
                  translateY: 86,
                  itemWidth: 100,
                  itemHeight: 22,
                  itemTextColor: "#999",
                  symbolSize: 18,
                  symbolShape: "circle"
                }
              ]}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default UniqueVisitorsInternational;
